<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import ButtonReplyBadge from 'dashboard/components-next/message/badges/ButtonReplyBadge.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { MESSAGE_TYPES, MESSAGE_VARIANTS } from '../../constants';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const {
  content,
  attachments,
  contentAttributes,
  messageType,
  status,
  variant,
} = useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

// Buttons the agent attached to an outgoing interactive WhatsApp message.
const whatsappButtons = computed(
  () => contentAttributes.value?.whatsappButtons || []
);

// A call-to-action button opens its url or dials its number; a quick reply is
// only actionable on the contact's device.
const isCallToAction = computed(
  () => contentAttributes.value?.whatsappInteractiveType === 'cta'
);

const buttonIcon = button => {
  if (button.type === 'url') return 'i-lucide-external-link';
  if (button.type === 'phone_number') return 'i-lucide-phone';
  return 'i-lucide-reply';
};

const openButtonTarget = button => {
  if (!isCallToAction.value) return;
  if (button.url) window.open(button.url, '_blank', 'noopener,noreferrer');
  else if (button.phone_number)
    window.location.href = `tel:${button.phone_number}`;
};

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const contactInfoRequestState = computed(() => {
  if (status.value === MESSAGE_STATUS.FAILED) return null;

  return contentAttributes.value?.whatsappContactInfo?.state;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <div class="flex flex-col gap-1">
    <BaseBubble class="px-4 py-3" data-bubble-name="text">
      <div class="gap-3 flex flex-col">
        <span v-if="isEmpty" class="text-n-slate-11">
          {{ $t('CONVERSATION.NO_CONTENT') }}
        </span>
        <ButtonReplyBadge :content-attributes="contentAttributes" />
        <FormattedContent v-if="renderContent" :content="renderContent" />
        <span
          v-if="contactInfoRequestState === 'pending'"
          class="text-xs font-medium text-n-slate-11"
        >
          {{ $t('CONVERSATION.REQUEST_CONTACT_INFO.STATES.PENDING') }}
        </span>
        <span
          v-else-if="contactInfoRequestState === 'shared'"
          class="text-xs font-medium text-n-slate-11"
        >
          {{ $t('CONVERSATION.REQUEST_CONTACT_INFO.STATES.SHARED') }}
        </span>
        <span
          v-else-if="contactInfoRequestState === 'identity_conflict'"
          class="text-xs font-medium text-n-slate-11"
        >
          {{ $t('CONVERSATION.REQUEST_CONTACT_INFO.STATES.IDENTITY_CONFLICT') }}
        </span>
        <TranslationToggle
          v-if="hasTranslations"
          class="-mt-3"
          :showing-original="renderOriginal"
          @toggle="handleSeeOriginal"
        />
        <AttachmentChips :attachments="attachments" class="gap-2" />
        <template v-if="isTemplate">
          <div
            v-if="contentAttributes.submittedEmail"
            class="px-2 py-1 rounded-lg bg-n-alpha-3"
          >
            {{ contentAttributes.submittedEmail }}
          </div>
        </template>
      </div>
    </BaseBubble>

    <div v-if="whatsappButtons.length" class="flex flex-col gap-1">
      <button
        v-for="button in whatsappButtons"
        :key="button.id"
        type="button"
        class="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium border rounded-xl border-n-strong text-n-blue-11"
        :class="[
          variant === MESSAGE_VARIANTS.ERROR
            ? 'text-n-ruby-11'
            : 'text-n-blue-11',
          isCallToAction
            ? 'cursor-pointer hover:bg-n-alpha-2'
            : 'cursor-default',
        ]"
        @click="openButtonTarget(button)"
      >
        <Icon :icon="buttonIcon(button)" class="size-4" />
        <span>{{ button.text }}</span>
      </button>
    </div>
  </div>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
